import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppointmentResponse } from '@store/generated/appointments';
import { ViewMode, DisplayMode, OrgScheduleFilterState } from '../types';

// Export AppointmentsState interface for use in store/types.ts
// This now contains ONLY client state - server state is managed by RTK Query
export interface AppointmentsState {
  // View control states (client UI state)
  viewMode: ViewMode;
  displayMode: DisplayMode;
  currentDate: string; // ISO string for serialization
  scrollToNowRequestId: number;

  // Calendar granularity settings (client UI state)
  calendarGranularity: {
    step: 1 | 5 | 10 | 15; // Precision for drag/resize in minutes
    timeslots: 1 | 2 | 3 | 4 | 6 | 12; // Number of slots grouped per visual row
  };

  // UI interaction state (client UI state)
  isSidebarOpen: boolean; // Drawer state for mobile
  editingAppointmentId: string | null; // Which appointment is being edited

  // Tutorial mode (client application state)
  isTutorialMode: boolean;
  mockAppointments: AppointmentResponse[]; // Only for tutorial mode

  // Org-level schedule filters (client UI state, not persisted)
  orgScheduleFilters: OrgScheduleFilterState;

  // Transient optimistic appointment patches keyed by appointment_id.
  optimisticAppointmentPatches: Record<string, Partial<AppointmentResponse>>;
}

// Initial state with sensible defaults - only client state
const initialState: AppointmentsState = {
  // View control states - defaults will be overridden by persistence
  viewMode: 'week',
  displayMode: 'calendar',
  currentDate: new Date().toISOString(),
  scrollToNowRequestId: 0,

  // Calendar granularity settings - defaults to hourly rows with 15-min precision
  calendarGranularity: {
    step: 15,
    timeslots: 4, // 4 * 15min = 60min rows
  },

  // UI interaction state
  isSidebarOpen: false, // Default closed on mobile, will be overridden on desktop
  editingAppointmentId: null, // No appointment being edited initially

  // Tutorial mode
  isTutorialMode: false,
  mockAppointments: [],

  // Org-level schedule filters (reset each session)
  orgScheduleFilters: {
    isOrgViewActive: false,
    selectedProviderIds: null,
    selectedPatientIds: [],
  },

  optimisticAppointmentPatches: {},
};

export const appointmentsSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    // View control actions (client UI state)
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },

    setDisplayMode: (state, action: PayloadAction<DisplayMode>) => {
      state.displayMode = action.payload;
    },

    setCurrentDate: (state, action: PayloadAction<string>) => {
      state.currentDate = action.payload;
    },

    requestScrollToNow: state => {
      state.scrollToNowRequestId += 1;
    },

    setCalendarGranularity: (
      state,
      action: PayloadAction<{ step: 1 | 5 | 10 | 15; timeslots: 1 | 2 | 3 | 4 | 6 | 12 }>,
    ) => {
      // Validate values before setting to prevent invalid states
      const validSteps = [1, 5, 10, 15];
      const validTimeslots = [1, 2, 3, 4, 6, 12];

      if (
        validSteps.includes(action.payload.step) &&
        validTimeslots.includes(action.payload.timeslots)
      ) {
        state.calendarGranularity = action.payload;
      } else {
        // Fallback to safe defaults if invalid
        console.warn('Invalid calendar granularity values, using defaults');
        state.calendarGranularity = { step: 15, timeslots: 4 };
      }
    },

    // UI interaction actions (client UI state)
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },

    setEditingAppointmentId: (state, action: PayloadAction<string | null>) => {
      state.editingAppointmentId = action.payload;
    },

    // Tutorial mode actions (client application state)
    setTutorialMode: (state, action: PayloadAction<boolean>) => {
      state.isTutorialMode = action.payload;
      if (!action.payload) {
        // Clear mock data when exiting tutorial mode
        state.mockAppointments = [];
      }
    },

    setMockAppointments: (state, action: PayloadAction<AppointmentResponse[]>) => {
      state.mockAppointments = action.payload;
    },

    // Org schedule filter actions (client UI state)
    setOrgViewActive: (state, action: PayloadAction<boolean>) => {
      state.orgScheduleFilters.isOrgViewActive = action.payload;
      if (!action.payload) {
        // Clear filters when switching back to personal view
        state.orgScheduleFilters.selectedProviderIds = null;
        state.orgScheduleFilters.selectedPatientIds = [];
      }
    },

    setSelectedProviderIds: (state, action: PayloadAction<string[] | null>) => {
      state.orgScheduleFilters.selectedProviderIds = action.payload;
    },

    setSelectedPatientIds: (state, action: PayloadAction<string[]>) => {
      state.orgScheduleFilters.selectedPatientIds = action.payload;
    },

    resetOrgScheduleFilters: state => {
      state.orgScheduleFilters = initialState.orgScheduleFilters;
    },

    setOptimisticAppointmentPatch: (
      state,
      action: PayloadAction<{
        appointmentId: string;
        patch: Partial<AppointmentResponse>;
      }>,
    ) => {
      const existing = state.optimisticAppointmentPatches[action.payload.appointmentId] ?? {};
      state.optimisticAppointmentPatches[action.payload.appointmentId] = {
        ...existing,
        ...action.payload.patch,
        block_state:
          existing.block_state && action.payload.patch.block_state
            ? {
                ...existing.block_state,
                ...action.payload.patch.block_state,
                qualifiers: {
                  ...existing.block_state.qualifiers,
                  ...action.payload.patch.block_state.qualifiers,
                },
              }
            : (action.payload.patch.block_state ?? existing.block_state),
      };
    },

    clearOptimisticAppointmentPatch: (state, action: PayloadAction<string>) => {
      delete state.optimisticAppointmentPatches[action.payload];
    },

    clearAllOptimisticAppointmentPatches: state => {
      state.optimisticAppointmentPatches = {};
    },

    // Reset actions
    resetAppointmentsState: () => initialState,
  },
});

// Export actions - only client state actions
export const {
  // View control actions
  setViewMode,
  setDisplayMode,
  setCurrentDate,
  requestScrollToNow,
  setCalendarGranularity,

  // UI interaction actions
  setSidebarOpen,
  setEditingAppointmentId,

  // Tutorial mode actions
  setTutorialMode,
  setMockAppointments,

  // Org schedule filter actions
  setOrgViewActive,
  setSelectedProviderIds,
  setSelectedPatientIds,
  resetOrgScheduleFilters,

  setOptimisticAppointmentPatch,
  clearOptimisticAppointmentPatch,
  clearAllOptimisticAppointmentPatches,

  // Reset actions
  resetAppointmentsState,
} = appointmentsSlice.actions;

// Export the reducer
export default appointmentsSlice.reducer;
